/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Bot } from 'lucide-react';
import type { AISettings } from '@shared/types/organizationSettings';

interface AISettingsSectionProps {
  settings: AISettings;
  onChange: (settings: AISettings) => void;
  onDirty: () => void;
}

export function AISettingsSection({ 
  settings, 
  onChange, 
  onDirty 
}: AISettingsSectionProps) {
  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Bot size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>AI Conflict Resolution</h3>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.enableAIResolution ?? true}
            onChange={(e) => {
              onChange({ ...settings, enableAIResolution: e.target.checked });
              onDirty();
            }}
          />
          <span style={{ fontSize: '14px' }}>Enable AI-assisted conflict resolution</span>
        </label>
      </div>
      
      {settings.enableAIResolution && (
        <div style={{ marginTop: '16px' }}>
          <div className="form-group">
            <label htmlFor="ai-threshold">Auto-resolve threshold</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <input
                id="ai-threshold"
                type="range"
                min={0.5}
                max={1}
                step={0.05}
                value={settings.autoResolveThreshold ?? 0.9}
                onChange={(e) => {
                  onChange({ ...settings, autoResolveThreshold: parseFloat(e.target.value) });
                  onDirty();
                }}
                style={{ flex: 1 }}
              />
              <span style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                minWidth: '50px',
                textAlign: 'right' 
              }}>
                {Math.round((settings.autoResolveThreshold ?? 0.9) * 100)}%
              </span>
            </div>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Auto-apply AI resolution when confidence exceeds this threshold
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="ai-model">Preferred AI Model</label>
            <select
              id="ai-model"
              value={settings.preferredModel || 'claude-sonnet'}
              onChange={(e) => {
                onChange({ ...settings, preferredModel: e.target.value });
                onDirty();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="claude-sonnet">Claude Sonnet</option>
              <option value="claude-opus">Claude Opus</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
